/**
 * TODO ENG-9217 @Ashutosh use it directly from mloop-shared once candidate portal works fine as candidate portal is using shared version
 * Not doing it right now as unit test is faling for some reason, will debug once candidate portal is working fine
 */
import { i18n } from '@modernloop/i18n';
import {
  getErrorStatus,
  getExternalErrorMessage,
  getInternalErrorMessage,
  logError,
  logMessage,
} from '@modernloop/shared/utils';
import { AxiosError } from 'axios';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { getAuth0Client } from 'src/contexts/auth0/utils';

// Track the active "reconnecting" snackbar key so we only show one at a time
// and can dismiss it when the connection is restored.
let reconnectingSnackbarKey: ReturnType<typeof enqueueSnackbar> | null = null;

function showReconnectingToast() {
  if (reconnectingSnackbarKey !== null) {
    return;
  }
  reconnectingSnackbarKey = enqueueSnackbar(i18n.t('common:shared.connection.reconnecting'), {
    variant: 'warning',
    persist: true,
    preventDuplicate: true,
  });
}

export function dismissReconnectingToast() {
  if (reconnectingSnackbarKey === null) {
    return;
  }
  closeSnackbar(reconnectingSnackbarKey);
  reconnectingSnackbarKey = null;
}

/**
 * Sometimes the error recieved is a `Network Error` this is our best guess at understanding when this is
 * @param error AxiosError that was recieved after the axios request was made
 * @returns boolean if its a Network Error
 */
function isNetworkError(err: AxiosError) {
  return !!err.isAxiosError && !err.response;
}

/**
 * This axios interceptor logs any error response with the appropriate tags and extra data
 *
 * @param error AxiosError that was recieved after the axios request was made
 * @returns void
 * @throws rethrows the error receieved
 */
const axiosInterceptorResponseError =
  () =>
  (error: AxiosError): void => {
    const internalError = getInternalErrorMessage(error);
    const externalError = getExternalErrorMessage(error);
    const endpoint = (error?.request as XMLHttpRequest)?.responseURL;

    logError(error, {
      endpoint,
      internalError,
      externalError,
      isNetworkError: isNetworkError(error),
      isAxiosError: error?.isAxiosError,
      request: error?.request,
      response: error?.response,
    });

    if (isNetworkError(error)) {
      // Connection refused / backend unreachable — show a persistent toast instead of
      // letting the error propagate silently or trigger a logout.
      showReconnectingToast();
      throw error;
    }

    // Any successful response means the connection is back — dismiss the reconnecting toast.
    dismissReconnectingToast();

    if (getErrorStatus(error) >= 500) {
      logMessage(`Customer received 5XX error at ${endpoint}`, 'fatal', { error });
    }

    if (getErrorStatus(error) === 401) {
      // eslint-disable-next-line promise/no-promise-in-callback
      getAuth0Client() // eslint-disable-next-line promise/always-return
        .then((auth0Client) => {
          setTimeout(() => {
            auth0Client.loginWithRedirect();
          }, 300);
        })
        .catch((err) => {
          logError(err);
        });
    }

    throw error;
  };

export default axiosInterceptorResponseError;
